// test start ENG-18988 prevCareCompare
import { IconUtils } from "@/shared/utils/IconUtils";
import Strings from "@/shared/utils/Strings.constants";
import { PetUnderwriterType } from "@spotpetinsurance/spot-types/entities/PetQuote";

type PreventiveBenefit = ReturnType<typeof IconUtils.PREVENTIVE_BENEFITS_PTZ_US>[number];
export type PreventiveBenefitsParams = Parameters<typeof IconUtils.PREVENTIVE_BENEFITS_PTZ_US>[0];

/**
 * Stable row identities for the side-by-side compare table.
 *
 * The table's row ORDER is a design decision per arm (see the brief's D3) and does not follow the
 * order of `IconUtils.PREVENTIVE_BENEFITS_PTZ_US`, so rows are addressed by id rather than by
 * position. Ids are resolved from the same `Strings` constants the benefit list builds its text
 * from, which keeps this experiment self-contained — nothing in the control path changes, and
 * teardown is deleting this folder.
 */
export type CompareRowId =
    | "dental"
    | "wellness-exam"
    | "deworming"
    | "heartworm-felv-test"
    | "fecal-test"
    | "rabies"
    | "dhlpp"
    | "bordetella"
    | "flea-heartworm-prevention"
    | "urinalysis"
    | "blood-test"
    | "health-certificate";

export interface CompareRow {
    id: CompareRowId;
    /** Benefit label. For `dental` this is the platinum wording, which already flips to "Spay/Neuter or…" under 3. */
    label: string;
    /** `null` = the plan does not include this benefit; renders as an em dash. */
    platinum: number | null;
    gold: number | null;
    /** Sub-label under the gold amount, e.g. the dental row's "Only Dental Cleaning*". */
    goldNote?: string;
}

const matchesSpeciesVariants = (text: string, group: Record<string, string>) => Object.values(group).includes(text);

const rowIdFor = (benefit: PreventiveBenefit): CompareRowId | null => {
    // Both dental items (gold "Dental Cleaning", platinum "Dental Cleaning or Spay/Neuter") live in
    // `procedures` and collapse into the single `dental` row.
    if (benefit.section === "procedures") return "dental";

    const { PREVENTIVE_ITEMS } = Strings;
    if (matchesSpeciesVariants(benefit.text, PREVENTIVE_ITEMS.DHLPP)) return "dhlpp";
    if (matchesSpeciesVariants(benefit.text, PREVENTIVE_ITEMS.TESTS)) return "heartworm-felv-test";
    if (matchesSpeciesVariants(benefit.text, PREVENTIVE_ITEMS.RABIES)) return "rabies";
    if (matchesSpeciesVariants(benefit.text, PREVENTIVE_ITEMS.BORDETELLA)) return "bordetella";

    switch (benefit.text) {
        case "Wellness Exam":
            return "wellness-exam";
        case "Deworming":
            return "deworming";
        case "Fecal Test":
            return "fecal-test";
        case "Flea/Heartworm Prevention":
            return "flea-heartworm-prevention";
        case "Health Certificate":
            return "health-certificate";
        case "Urinalysis":
            return "urinalysis";
        case "Blood Test":
            return "blood-test";
        default:
            return null;
    }
};

/**
 * Collapses the per-plan benefit lists into one row per benefit, carrying a cell for each plan.
 * A benefit a plan does not include gets `null`, which the table renders as an em dash.
 */
export const buildCompareRows = (underwriter: PetUnderwriterType, params: PreventiveBenefitsParams): Map<CompareRowId, CompareRow> => {
    const benefits = underwriter === "ptz-us" ? IconUtils.PREVENTIVE_BENEFITS_PTZ_US(params) : [];
    const rows = new Map<CompareRowId, CompareRow>();

    benefits.forEach(benefit => {
        const id = rowIdFor(benefit);
        if (!id) return;

        const existing = rows.get(id);
        const includesPlatinum = benefit.level.includes("platinum");
        const includesGold = benefit.level.includes("gold");

        rows.set(id, {
            id,
            // the platinum wording wins the merged dental row; otherwise the first (only) label
            label: includesPlatinum || !existing ? benefit.text : existing.label,
            platinum: includesPlatinum ? benefit.amountPlatinum : (existing?.platinum ?? null),
            gold: includesGold ? benefit.amountGold : (existing?.gold ?? null),
            goldNote: id === "dental" ? Strings.PREVENTIVE_COMPARE.GOLD_DENTAL_NOTE : undefined
        });
    });

    return rows;
};

export type CompareGroupId = "treatments" | "tests" | "vaccines";

export interface CompareGroup {
    id: CompareGroupId;
    /** Shown from `sm`; `shortTitle` is used below it. */
    title: string;
    shortTitle: string;
    rowIds: CompareRowId[];
}

/** `compareTableCategorized`: three groups, collapsing control's five sections. */
export const COMPARE_GROUPS: CompareGroup[] = [
    {
        id: "treatments",
        title: Strings.PREVENTIVE_COMPARE.GROUPS.TREATMENTS,
        shortTitle: Strings.PREVENTIVE_COMPARE.GROUPS.TREATMENTS,
        rowIds: ["dental", "deworming", "flea-heartworm-prevention"]
    },
    {
        id: "tests",
        title: Strings.PREVENTIVE_COMPARE.GROUPS.TESTS,
        shortTitle: Strings.PREVENTIVE_COMPARE.GROUPS.TESTS_SHORT,
        rowIds: ["wellness-exam", "heartworm-felv-test", "fecal-test", "health-certificate", "urinalysis", "blood-test"]
    },
    {
        id: "vaccines",
        title: Strings.PREVENTIVE_COMPARE.GROUPS.VACCINES,
        shortTitle: Strings.PREVENTIVE_COMPARE.GROUPS.VACCINES,
        rowIds: ["rabies", "dhlpp", "bordetella"]
    }
];

/** `compareTable`: no groups — benefits both plans share first, then platinum-only. */
export const COMPARE_FLAT_ORDER: CompareRowId[] = [
    "dental",
    "wellness-exam",
    "deworming",
    "heartworm-felv-test",
    "fecal-test",
    "rabies",
    "dhlpp",
    "bordetella",
    "flea-heartworm-prevention",
    "urinalysis",
    "blood-test",
    "health-certificate"
];
// test end
